import React from 'react';
import { Target, Eye } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-32 px-6 bg-gradient-to-b from-[#1e3a8a] to-[#2563eb]">
        <div className="max-w-5xl mx-auto text-center text-white">
          <h1 className="text-6xl font-bold mb-6">About BRISE</h1>
          <p className="text-2xl text-gray-100">
            Building a Self-Reliant Solar Future for Bharat
          </p>
        </div>
      </section>

      {/* Company Profile */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold text-[#1e3a8a] mb-6">
                Building a Self-Reliant Solar Future
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                At BRISE, we believe energy is the engine of progress. Headquartered in India, 
                we carry on the business of manufacturing, assembling, and distributing top-tier 
                renewable energy equipment.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed">
                We serve government entities, the public sector, and private industries with a 
                singular focus: <span className="font-bold text-[#1e3a8a]">Quality</span>.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1624397640148-949b1732bb0a?w=800&q=85" 
                alt="Solar Installation Work" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24 px-6 bg-[#1e3a8a] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Vision */}
            <div className="bg-white/10 backdrop-blur-sm p-12 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-[#fbbf24] rounded-xl flex items-center justify-center mb-6">
                <Eye className="h-8 w-8 text-[#1e3a8a]" />
              </div>
              <h3 className="text-3xl font-bold mb-6">Our Vision</h3>
              <p className="text-xl text-gray-100 leading-relaxed">
                To be a global leader in renewable energy infrastructure, establishing Bharat 
                as a hub of solar excellence.
              </p>
            </div>

            {/* Mission */}
            <div className="bg-white/10 backdrop-blur-sm p-12 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-[#fbbf24] rounded-xl flex items-center justify-center mb-6">
                <Target className="h-8 w-8 text-[#1e3a8a]" />
              </div>
              <h3 className="text-3xl font-bold mb-6">Our Mission</h3>
              <p className="text-xl text-gray-100 leading-relaxed">
                To accelerate the global transition to green energy by delivering high-quality 
                solar manufacturing and world-class EPC services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Does BRISE Mean Section */}
      <section className="py-24 px-6 bg-white relative overflow-hidden">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 pattern-grid opacity-30"></div>
        
        {/* Decorative circles */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-[#fbbf24]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#1e3a8a]/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1e3a8a] mb-6 animate-fade-in">What Does BRISE Mean?</h2>
            <p className="text-xl text-gray-700 animate-fade-in" style={{animationDelay: '0.2s'}}>A name that reflects our mission and vision</p>
          </div>

          <div className="grid md:grid-cols-5 gap-6 max-w-6xl mx-auto">
            {/* B */}
            <div className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] p-8 rounded-2xl text-center transform hover:scale-110 hover:-rotate-2 transition-all duration-300 shadow-lg hover:shadow-2xl relative group">
              <div className="absolute inset-0 bg-[#fbbf24]/0 group-hover:bg-[#fbbf24]/10 rounded-2xl transition-all duration-300"></div>
              <div className="text-6xl font-bold text-[#fbbf24] mb-4 relative z-10">B</div>
              <div className="text-white text-lg font-semibold relative z-10">BHARAT</div>
              <div className="text-gray-200 text-sm mt-2 relative z-10">India</div>
            </div>

            {/* R */}
            <div className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] p-8 rounded-2xl text-center transform hover:scale-110 hover:-rotate-2 transition-all duration-300 shadow-lg hover:shadow-2xl relative group">
              <div className="absolute inset-0 bg-[#fbbf24]/0 group-hover:bg-[#fbbf24]/10 rounded-2xl transition-all duration-300"></div>
              <div className="text-6xl font-bold text-[#fbbf24] mb-4 relative z-10">R</div>
              <div className="text-white text-lg font-semibold relative z-10">RENEWABLE</div>
              <div className="text-gray-200 text-sm mt-2 relative z-10">Sustainable</div>
            </div>

            {/* I */}
            <div className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] p-8 rounded-2xl text-center transform hover:scale-110 hover:-rotate-2 transition-all duration-300 shadow-lg hover:shadow-2xl relative group">
              <div className="absolute inset-0 bg-[#fbbf24]/0 group-hover:bg-[#fbbf24]/10 rounded-2xl transition-all duration-300"></div>
              <div className="text-6xl font-bold text-[#fbbf24] mb-4 relative z-10">I</div>
              <div className="text-white text-lg font-semibold relative z-10">INFRASTRUCTURE</div>
              <div className="text-gray-200 text-sm mt-2 relative z-10">Foundation</div>
            </div>

            {/* S */}
            <div className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] p-8 rounded-2xl text-center transform hover:scale-110 hover:-rotate-2 transition-all duration-300 shadow-lg hover:shadow-2xl relative group">
              <div className="absolute inset-0 bg-[#fbbf24]/0 group-hover:bg-[#fbbf24]/10 rounded-2xl transition-all duration-300"></div>
              <div className="text-6xl font-bold text-[#fbbf24] mb-4 relative z-10">S</div>
              <div className="text-white text-lg font-semibold relative z-10">SOLAR</div>
              <div className="text-gray-200 text-sm mt-2 relative z-10">Clean Power</div>
            </div>

            {/* E */}
            <div className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] p-8 rounded-2xl text-center transform hover:scale-110 hover:-rotate-2 transition-all duration-300 shadow-lg hover:shadow-2xl relative group">
              <div className="absolute inset-0 bg-[#fbbf24]/0 group-hover:bg-[#fbbf24]/10 rounded-2xl transition-all duration-300"></div>
              <div className="text-6xl font-bold text-[#fbbf24] mb-4 relative z-10">E</div>
              <div className="text-white text-lg font-semibold relative z-10">ENERGY</div>
              <div className="text-gray-200 text-sm mt-2 relative z-10">Future</div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-2xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
              Each letter represents our commitment to building a <span className="text-[#fbbf24] font-bold">self-reliant</span>, 
              <span className="text-[#1e3a8a] font-bold"> sustainable energy future</span> for India
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center text-[#1e3a8a] mb-16">
            Our Core Values
          </h2>
          
          <div className="grid md:grid-cols-3 gap-10">
            <div className="text-center">
              <div className="w-24 h-24 bg-[#fbbf24] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl font-bold text-[#1e3a8a]">Q</span>
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4">Quality</h3>
              <p className="text-lg text-gray-700">
                Excellence in every component, every project, every interaction
              </p>
            </div>

            <div className="text-center">
              <div className="w-24 h-24 bg-[#fbbf24] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl font-bold text-[#1e3a8a]">I</span>
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4">Innovation</h3>
              <p className="text-lg text-gray-700">
                Pioneering sustainable solutions for tomorrow's challenges
              </p>
            </div>

            <div className="text-center">
              <div className="w-24 h-24 bg-[#fbbf24] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl font-bold text-[#1e3a8a]">R</span>
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4">Reliability</h3>
              <p className="text-lg text-gray-700">
                Trusted partner for India's renewable energy transformation
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose BRISE */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center text-[#1e3a8a] mb-16">
            Why Choose BRISE?
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            <div className="relative bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg border-t-4 border-[#fbbf24]">
              <div className="absolute -top-6 left-8 w-12 h-12 bg-[#fbbf24] rounded-lg flex items-center justify-center">
                <span className="text-2xl font-bold text-[#1e3a8a]">1</span>
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4 mt-6">ISO Certified</h3>
              <p className="text-gray-700 leading-relaxed">
                Internationally certified manufacturing processes ensuring world-class quality standards
              </p>
            </div>
            <div className="relative bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg border-t-4 border-[#fbbf24]">
              <div className="absolute -top-6 left-8 w-12 h-12 bg-[#fbbf24] rounded-lg flex items-center justify-center">
                <span className="text-2xl font-bold text-[#1e3a8a]">2</span>
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4 mt-6">25-Year Warranty</h3>
              <p className="text-gray-700 leading-relaxed">
                Industry-leading warranty on all solar modules and comprehensive system support
              </p>
            </div>
            <div className="relative bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg border-t-4 border-[#fbbf24]">
              <div className="absolute -top-6 left-8 w-12 h-12 bg-[#fbbf24] rounded-lg flex items-center justify-center">
                <span className="text-2xl font-bold text-[#1e3a8a]">3</span>
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4 mt-6">Expert Team</h3>
              <p className="text-gray-700 leading-relaxed">
                50+ certified engineers with decades of combined experience in renewable energy
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <img 
              src="https://images.unsplash.com/photo-1768839727824-28d6f0dcd1d1?w=800&q=85" 
              alt="Solar Field" 
              className="rounded-2xl shadow-lg h-96 w-full object-cover"
            />
            <img 
              src="https://images.unsplash.com/photo-1748347687685-5613deb470ec?w=800&q=85" 
              alt="Solar Inspection" 
              className="rounded-2xl shadow-lg h-96 w-full object-cover"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
